import React from 'react';
import { useNavigate } from 'react-router-dom';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CodeIcon from '@mui/icons-material/Code';
import ConstructionIcon from '@mui/icons-material/Construction';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type ArticleEntry = {
  id: string;
  title: string;
  path: string;
};

type SeriesData = {
  key: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  target: string;
  articles: ArticleEntry[];
};

const seriesData: SeriesData[] = [
  {
    key: 'basics',
    icon: <ConstructionIcon />,
    title: 'Kiro基本編',
    description: 'Kiroの仕組みとワークフローの全体像を理解する',
    color: '#546e7a',
    target: '全員（初めてKiroを使う技術者）',
    articles: [
      {
        id: 'Part1',
        title: 'Kiroとは: AIとの対話の基本原則',
        path: '/devdocs/workflow/kiro-basics-1',
      },
      {
        id: 'Part2',
        title: 'Steeringファイル: AIにルールを伝える方法',
        path: '/devdocs/workflow/kiro-basics-2',
      },
      {
        id: 'Part3',
        title: '機能と運用: 知っておくべきKiroの機能',
        path: '/devdocs/workflow/kiro-basics-3',
      },
      {
        id: 'Part4',
        title: 'ワークフロー全体像: 本プロジェクトの進め方',
        path: '/devdocs/workflow/kiro-basics-4',
      },
    ],
  },
  {
    key: 'requirements',
    icon: <NoteAltIcon />,
    title: '要件定義編',
    description: 'AIと対話しながら要件を固める方法を学ぶ',
    color: '#1565c0',
    target: 'プロダクトオーナー、テックリード',
    articles: [
      { id: 'Part1', title: 'RFPを作成する', path: '/devdocs/workflow/req-step1' },
      { id: 'Part2', title: 'AI対話で仕様を策定する', path: '/devdocs/workflow/req-step2' },
      {
        id: 'Part3',
        title: 'proposal/businessを出力する',
        path: '/devdocs/workflow/req-step3',
      },
      {
        id: 'Part4',
        title: 'システムユースケースを出力する',
        path: '/devdocs/workflow/req-step4',
      },
    ],
  },
  {
    key: 'backend',
    icon: <CodeIcon />,
    title: 'バックエンド開発編',
    description: '設計→コード生成→テストの自動化ワークフロー',
    color: '#2e7d32',
    target: 'バックエンドエンジニア',
    articles: [
      {
        id: 'Part1',
        title: 'スキーマ定義とAPI仕様書を作成する',
        path: '/devdocs/workflow/be-step1',
      },
      {
        id: 'Part2',
        title: 'API実装ワークフローとその全体像',
        path: '/devdocs/workflow/be-step2',
      },
      {
        id: 'Part3',
        title: 'API実装ワークフロー Phase1: DD書の作成',
        path: '/devdocs/workflow/be-step3',
      },
      {
        id: 'Part4',
        title: 'API実装ワークフロー Phase2: コード生成',
        path: '/devdocs/workflow/be-step4',
      },
      {
        id: 'Part5',
        title: 'API実装ワークフロー Phase3: 単体テスト',
        path: '/devdocs/workflow/be-step5',
      },
      { id: 'Part6', title: 'E2Eテスト', path: '/devdocs/workflow/be-step6' },
      { id: '付録', title: '成果物サンプル集', path: '/devdocs/workflow/be-appendix' },
    ],
  },
];

const WorkflowHome: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Stack spacing={3} sx={{ maxWidth: 860, pb: 4 }}>
      {/* パンくず */}
      <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="text.secondary"
          onClick={() => navigate('/devdocs')}
          sx={{ cursor: 'pointer' }}
        >
          Dev Insights
        </Link>
        <Typography variant="body2" color="text.primary">
          AIと作る、壊れないバックエンド
        </Typography>
      </Breadcrumbs>

      {/* ヒーロー */}
      <Box
        sx={{
          borderRadius: 3,
          p: 4,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1b5e20 0%, #004d40 100%)'
              : 'linear-gradient(135deg, #2e7d32 0%, #00695c 100%)',
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>
          AIと作る、壊れないバックエンド
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}
        >
          バイブコーディングで終わらせない — 認知負債を生まないAI協調開発
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 1.5, color: 'rgba(255,255,255,0.85)', maxWidth: 620 }}
        >
          「動くけど誰も読めないコード」を量産しないために。設計書を起点にAIを制御し、人間がレビューで品質を担保する。バイブコーディングの先にある、トレーサブルなAI駆動開発を体系的に学べる全15記事です。
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <MetaBadge label="15記事" />
          <MetaBadge label="通読 約3時間" />
          <MetaBadge label="実践 約5〜6時間" />
        </Stack>
      </Box>

      {/* メッセージ */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 2.5,
          bgcolor: 'action.hover',
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2" fontWeight={600}>
            このシリーズを読む前に
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.9 }}>
            生成AIを活用した開発にはまだデファクトスタンダードと呼べるものがありません。モデルの進化が速く、半年前のベストプラクティスが今日も有効とは限らないためです。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.9 }}>
            本シリーズで紹介するプラクティスやアンチパターンは、このプロジェクトの開発を通じて試行錯誤した結果たどり着いた「ある時点でのスナップショット」であり、唯一の正解ではありません。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.9 }}>
            大事なのは、ここに書かれたことを鵜呑みにせず、自分のプロジェクトで実際に試し、フィードバックループを回しながら自分たちのプラクティスを育てていくことです。参考にはできるけれど、正解は自分で模索していく
            — そのスタンスで読んでもらえればと思います。
          </Typography>
        </Stack>
      </Box>

      {/* 前提知識 */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="body2" fontWeight={600}>
            前提知識
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Chip label="Java / Spring Boot 基礎" size="small" variant="outlined" />
            <Chip label="Git操作" size="small" variant="outlined" />
            <Chip label="Markdown" size="small" variant="outlined" />
          </Stack>
        </Stack>
      </Box>

      <Divider />

      {/* シリーズ一覧 */}
      <Stack spacing={2.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          シリーズ一覧
        </Typography>
        {seriesData.map((series) => (
          <SeriesCard key={series.key} series={series} onNavigate={navigate} />
        ))}
      </Stack>
    </Stack>
  );
};

// --- Sub components ---

const MetaBadge: React.FC<{ label: string }> = ({ label }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <AccessTimeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
      {label}
    </Typography>
  </Stack>
);

type SeriesCardProps = {
  series: SeriesData;
  onNavigate: (path: string) => void;
};

const SeriesCard: React.FC<SeriesCardProps> = ({ series, onNavigate }) => {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* シリーズヘッダー */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${series.color}14`,
              color: series.color,
            }}
          >
            {React.cloneElement(series.icon as React.ReactElement<{ sx?: object }>, {
              sx: { fontSize: 20 },
            })}
          </Box>
          <Stack spacing={0.25} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontWeight={600}>
                {series.title}
              </Typography>
              <Chip
                label={`${series.articles.length}記事`}
                size="small"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {series.description}
            </Typography>
          </Stack>
          <Chip
            label={series.target}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 22 }}
          />
        </Stack>
      </Box>

      {/* 記事一覧 */}
      <Box sx={{ p: 1.5 }}>
        <Grid container spacing={1.5}>
          {series.articles.map((article) => (
            <Grid key={article.id} size={{ xs: 12, sm: 6 }}>
              <ButtonBase
                onClick={() => onNavigate(article.path)}
                sx={{
                  width: '100%',
                  display: 'block',
                  textAlign: 'left',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 1.5,
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: series.color },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={article.id}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        bgcolor: `${series.color}14`,
                        color: series.color,
                      }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      {article.title}
                    </Typography>
                  </Stack>
                  <ArrowForwardIosIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                </Stack>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default WorkflowHome;
